export const COMPANY_FIELDS = ['company_name', 'inn', 'kpp', 'ogrn', 'legal_address', 'director_name', 'director_position'];

export function companyKind(clientType) {
  if (['legal', 'ooo'].includes(clientType)) return 'LEGAL';
  if (['ip', 'ip_npd'].includes(clientType)) return 'INDIVIDUAL';
  return null;
}

export function validInn(value) {
  const inn = String(value || '');
  if (!/^(?:\d{10}|\d{12})$/.test(inn) || /^0+$/.test(inn)) return false;
  const digits = [...inn].map(Number);
  const check = weights => weights.reduce((sum, weight, index) => sum + weight * digits[index], 0) % 11 % 10;
  return inn.length === 10
    ? check([2, 4, 10, 3, 5, 9, 4, 6, 8]) === digits[9]
    : check([7, 2, 4, 10, 3, 5, 9, 4, 6, 8]) === digits[10]
      && check([3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8]) === digits[11];
}

export function companyPatch(suggestion) {
  return Object.fromEntries(COMPANY_FIELDS.filter(key => typeof suggestion?.[key] === 'string' && suggestion[key].trim())
    .map(key => [key, suggestion[key].trim()]));
}

export function companyConflicts(form, patch) {
  return Object.keys(patch).filter(key => String(form?.[key] || '').trim()
    && String(form[key]).trim() !== patch[key]);
}

export function mergeCompany(form, patch, replace = false) {
  const next = { ...form };
  for (const [key, value] of Object.entries(patch)) {
    if (COMPANY_FIELDS.includes(key) && (replace || !String(next[key] || '').trim())) next[key] = value;
  }
  return next;
}

export async function lookupCompany(apiURL, { inn, kind, token, audience = 'staff', signal }) {
  const response = await fetch(`${String(apiURL).replace(/\/$/, '')}/main/api/client/company-lookup/`, {
    method: 'POST', signal, credentials: 'omit',
    headers: { 'Content-Type': 'application/json', Authorization: `${audience === 'client' ? 'ClientToken' : 'Bearer'} ${token}` },
    body: JSON.stringify({ inn, kind }),
  });
  let body;
  try { body = await response.json(); } catch (_) { body = null; }
  if (!response.ok) {
    const readable = [body?.message, body?.detail, body?.detail?.detail].find(value => typeof value === 'string' && value.trim());
    const retry = Number(body?.retry_after ?? response.headers?.get?.('Retry-After'));
    const fallback = response.status === 401 ? 'Сессия истекла. Войдите снова, чтобы получить реквизиты.'
      : response.status === 403 ? 'Нет доступа к подсказкам реквизитов.'
      : response.status === 429 ? `Слишком частые запросы. Повторите ${Number.isFinite(retry) && retry > 0 ? `через ${retry} с.` : 'немного позже.'}`
      : 'Не удалось получить реквизиты. Заполните их вручную.';
    throw new Error(response.status === 429 ? fallback : readable || fallback);
  }
  return body || {};
}

// The document seller has different field names. Preserve its legal form,
// short name, bank fields and other settings when applying a company patch.
export function sellerAsCompany(seller) {
  return { ...seller, company_name: seller?.name || '', client_type: seller?.legal_form || '' };
}

export function applySellerCompany(seller, update) {
  const company = typeof update === 'function' ? update(sellerAsCompany(seller)) : update;
  const next = { ...seller };
  for (const key of COMPANY_FIELDS) {
    if (key !== 'company_name' && typeof company?.[key] === 'string') next[key] = company[key];
  }
  if (typeof company?.company_name === 'string') next.name = company.company_name;
  return next;
}
